import { useEffect, useRef, useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  NachbereitungIntake,
  nachbereitungPrompt,
} from "@/components/nachbereitung-intake"
import { GeneralCoachChat } from "@/components/coach-chat-general"
import { toast } from "sonner"
import { useCoachThread, useCreateCoachThread } from "@/lib/queries"
import { useCoach } from "@/lib/use-coach"
import { useSessionContext } from "@/lib/session"
import { parseChatSearch } from "@/lib/chat-navigation"
import { threadDestination } from "@/lib/recent-entries"
import { NACHBEREITUNG_START_PROMPTS } from "@/lib/coach-prompts"
import { COACH_FALLBACK_LABEL } from "@nl/shared"

/**
 * Die Nachbereitung eines real geführten Gesprächs.
 *
 * Eigener Einstieg und ausdrücklich kein Teil eines Durchlaufs: es gibt keine
 * Persona, keinen Fall und keine vorherige Simulation. Der Aufbau spiegelt den
 * Gesprächsauftakt, weil es dieselbe Bewegung in die andere Richtung ist —
 * dort vor dem Termin, hier danach.
 */
export function NachbereitungPage() {
  const { role } = useSessionContext()
  const coach = useCoach()
  const name = coach?.name ?? COACH_FALLBACK_LABEL
  const navigate = useNavigate()
  const { gespraech } = Route.useSearch()
  const [initialPrompt, setInitialPrompt] = useState<string>()
  const [voiceThreadId, setVoiceThreadId] = useState<string>()
  const createThread = useCreateCoachThread()
  const previousThread = useRef(gespraech)
  useEffect(() => {
    if (previousThread.current && previousThread.current !== gespraech)
      setInitialPrompt(undefined)
    previousThread.current = gespraech
  }, [gespraech])
  const { data: thread } = useCoachThread(gespraech)
  // A newly created empty thread receives its mode with the first message.
  const differentMode = thread?.mode && thread.mode !== "nachbereitung"
  useEffect(() => {
    if (differentMode)
      void navigate({ ...threadDestination(thread), replace: true })
  }, [differentMode, thread, navigate])
  useEffect(() => {
    if (role === "coach") void navigate({ to: "/coach", replace: true })
    if (role === "manager") void navigate({ to: "/management", replace: true })
  }, [role, navigate])
  if (differentMode) return null
  if (!gespraech && !initialPrompt)
    return (
      <NachbereitungIntake
        coachName={name}
        pending={createThread.isPending}
        onStart={async (context, channel) => {
          if (channel === "chat") {
            setInitialPrompt(nachbereitungPrompt(context))
            return
          }
          try {
            const created = await createThread.mutateAsync({
              body: {
                startContext: nachbereitungPrompt(context),
                startMode: "nachbereitung",
              },
            })
            setVoiceThreadId(created.id)
            await navigate({
              to: "/nachbereitung",
              search: { gespraech: created.id },
              replace: true,
            })
          } catch {
            toast.error(
              "Das Live-Gespräch konnte nicht vorbereitet werden. Deine Angaben bleiben erhalten. Bitte versuche es erneut oder nutze den Chat."
            )
          }
        }}
      />
    )
  return (
    <GeneralCoachChat
      threadId={gespraech}
      initialPrompt={initialPrompt}
      autoStartVoice={!!gespraech && gespraech === voiceThreadId}
      mode="nachbereitung"
      barSize="md"
      autoFocus
      onThreadCreated={(id) =>
        void navigate({
          to: "/nachbereitung",
          search: { gespraech: id },
          replace: true,
        })
      }
      onReset={() => {
        setInitialPrompt(undefined)
        setVoiceThreadId(undefined)
        void navigate({ to: "/nachbereitung", search: {}, replace: true })
      }}
      aboveThread={
        <div className="mb-6">
          <h1 className="text-base font-medium">Nachbereitung</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Dein geführtes Gespräch, gemeinsam mit {name} nachbesprochen.
          </p>
        </div>
      }
      empty={{
        title: "Welches Gespräch möchtest du nachbesprechen?",
        hint: `Erzähl kurz, worum es ging und wie es gelaufen ist. ${name} war nicht dabei und fragt nach, bevor er etwas einordnet. Bitte anonymisiert, also ohne Namen oder andere Kundendaten.`,
        prompts: NACHBEREITUNG_START_PROMPTS,
      }}
      placeholder="Zum Beispiel: Gestern lief ein Vorsorgegespräch anders als geplant …"
    />
  )
}

export const Route = createFileRoute("/_app/nachbereitung")({
  validateSearch: (
    search: Record<string, unknown>
  ): { gespraech?: string } => ({
    gespraech: parseChatSearch(search).gespraech,
  }),
  component: NachbereitungPage,
})
